import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'motion/react';
import ChatVisualNovel from './templates/ChatVisualNovel';
import { loadGameData, isStandalone, getStandaloneTeamId } from './utils/dataProvider';
import { API_BASE_URL } from '../utils/api';
import PwaDownloadOverlay from './PwaDownloadOverlay';
import PwaInstallHint from './PwaInstallHint';

/* ── Play From Here: decision configuration dialog ─────────── */

function findReachableDecisionsBefore(entryNodeId, storyflowData) {
  if (!storyflowData?.graph) return [];
  const nodes = storyflowData.graph.nodes || [];
  const edges = storyflowData.graph.edges || [];

  // Build reverse adjacency: target → sources
  const reverseAdj = {};
  for (const e of edges) {
    if (!reverseAdj[e.target]) reverseAdj[e.target] = [];
    reverseAdj[e.target].push(e.source);
  }

  // BFS backwards from entryNode
  const visited = new Set();
  const queue = [entryNodeId];
  // Include entryNode itself (it might be a decision)
  visited.add(entryNodeId);
  while (queue.length > 0) {
    const id = queue.shift();
    for (const src of (reverseAdj[id] || [])) {
      if (!visited.has(src)) {
        visited.add(src);
        queue.push(src);
      }
    }
  }

  // Collect decision nodes from visited set
  return nodes
    .filter((n) => visited.has(n.id) && n.type === 'sfDecision' && n.data?.decisionName)
    .map((n) => ({
      nodeId: n.id,
      decisionName: n.data.decisionName,
      options: n.data.options || ['Option A', 'Option B']
    }));
}

function PlayFromHereDialog({ teamId, entryStoryflow, entryNode, allStoryflows, onStart }) {
  const [apiDecisions, setApiDecisions] = useState([]);
  const [chosenValues, setChosenValues] = useState({});
  const [loaded, setLoaded] = useState(false);

  // Find the storyflow data for the entry
  const sfData = useMemo(
    () => allStoryflows.find((s) => s.id === entryStoryflow),
    [allStoryflows, entryStoryflow]
  );
  const currentChapterNum = sfData?.number ?? 0;

  // Decisions reachable before entryNode in the current graph
  const localDecisions = useMemo(
    () => findReachableDecisionsBefore(entryNode, sfData),
    [entryNode, sfData]
  );

  // Load previous chapter decisions from API (skip in standalone — decisions come from bundle)
  useEffect(() => {
    if (!teamId || isStandalone()) { setLoaded(true); return; }
    const token = sessionStorage.getItem('flowcore_session_token') || '';
    fetch(`${API_BASE_URL}/api/decisions?teamId=${encodeURIComponent(teamId)}&upToChapter=${currentChapterNum - 1}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((r) => r.ok ? r.json() : { decisions: [] })
      .then((data) => {
        setApiDecisions(data.decisions || []);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [teamId, currentChapterNum]);

  // Merge: API decisions (prev chapters) + local decisions (current graph before node)
  // Deduplicate by decisionName (keep last)
  const allDecisions = useMemo(() => {
    const map = new Map();
    for (const d of apiDecisions) {
      map.set(d.decisionName, { ...d, chapterNumber: d.chapterNumber ?? 0 });
    }
    for (const d of localDecisions) {
      if (!map.has(d.decisionName)) {
        map.set(d.decisionName, { ...d, chapterNumber: currentChapterNum });
      }
    }
    return Array.from(map.values()).sort((a, b) => a.chapterNumber - b.chapterNumber);
  }, [apiDecisions, localDecisions, currentChapterNum]);

  // Initialize default values (first option for each)
  useEffect(() => {
    const defaults = {};
    for (const d of allDecisions) {
      if (!chosenValues[d.decisionName]) {
        const opts = d.options || [];
        const isTF = opts.length === 2 && opts[0] === 'True' && opts[1] === 'False';
        defaults[d.decisionName] = isTF ? 'False' : '';
      }
    }
    if (Object.keys(defaults).length > 0) {
      setChosenValues((prev) => ({ ...defaults, ...prev }));
    }
  }, [allDecisions]);

  const setValue = useCallback((name, value) => {
    setChosenValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  return (
    <div className="game-shell h-full w-full flex items-center justify-center bg-game-surface font-game select-none" onContextMenu={(e) => e.preventDefault()}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-96 max-w-[calc(100%-32px)] rounded-2xl border border-white/10 bg-game-canvas shadow-2xl flex flex-col max-h-[80vh]"
      >
        <div className="p-6 pb-3 shrink-0">
          <h2 className="text-lg font-bold text-game-text mb-1">Play From Here</h2>
          <p className="text-sm text-game-body">
            {allDecisions.length > 0
              ? 'Configure past decisions before starting.'
              : 'No decisions to configure.'}
          </p>
        </div>

        {!loaded ? (
          <div className="px-6 pb-4 text-xs text-game-muted">Loading decisions...</div>
        ) : allDecisions.length > 0 ? (
          <div className="flex-1 overflow-y-auto px-6 pb-2" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.15) transparent' }}>
            <div className="rounded-lg border border-white/5 bg-game-surface/50 divide-y divide-white/5">
              {allDecisions.map((d) => {
                const opts = d.options || [];
                const isTF = opts.length === 2 && opts[0] === 'True' && opts[1] === 'False';
                const current = chosenValues[d.decisionName] || opts[0] || '';
                return (
                  <div key={d.decisionName} className="px-3 py-2.5">
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-semibold text-game-text">
                        <span className="text-game-muted mr-1">Ch.{d.chapterNumber}</span>
                        {d.decisionName}
                      </div>
                      {isTF && (
                        <button
                          type="button"
                          onClick={() => setValue(d.decisionName, current === 'True' ? 'False' : 'True')}
                          className="relative w-10 h-5 rounded-full transition-colors shrink-0 ml-2"
                          style={{ backgroundColor: current === 'True' ? '#8b5cf6' : 'rgba(255,255,255,0.12)' }}
                        >
                          <div
                            className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform"
                            style={{ left: current === 'True' ? 22 : 2 }}
                          />
                        </button>
                      )}
                    </div>
                    {!isTF && (
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5">
                        {opts.map((opt) => (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => setValue(d.decisionName, opt)}
                            className="flex items-center gap-1.5 py-0.5 text-xs transition-colors"
                          >
                            <div
                              className="w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center shrink-0"
                              style={{ borderColor: current === opt ? '#8b5cf6' : 'rgba(255,255,255,0.2)' }}
                            >
                              {current === opt && (
                                <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#8b5cf6' }} />
                              )}
                            </div>
                            <span className={current === opt ? 'text-white font-medium' : 'text-game-muted'}>{opt}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="px-6 pb-2">
            <div className="rounded-lg border border-white/5 bg-game-surface/50 p-3">
              <p className="text-xs text-game-muted italic">No decisions before this node.</p>
            </div>
          </div>
        )}

        <div className="p-6 pt-3 shrink-0">
          <button
            type="button"
            onClick={() => onStart(chosenValues)}
            className="w-full rounded-xl bg-game-accent py-2.5 text-sm font-semibold text-game-accentText hover:bg-game-accentHover transition-colors"
          >
            Continue
          </button>
        </div>
      </motion.div>
    </div>
  );
}

/**
 * Game shell — entry point rendered at /game route.
 * Reads teamId, entryStoryflow, entryNode from query params.
 * Loads ALL storyflows (with graphs) + characters before starting.
 * If entryStoryflow+entryNode are set, shows a "variables" dialog before starting.
 * Otherwise loads from a save slot or starts fresh.
 */
export default function GameShell() {
  const [searchParams] = useSearchParams();
  const paramTeamId = searchParams.get('teamId') || '';
  const entryStoryflow = searchParams.get('entryStoryflow') || '';
  const entryNode = searchParams.get('entryNode') || '';

  // In standalone mode, use the embedded teamId (or resolve from bundle later)
  const [resolvedTeamId, setResolvedTeamId] = useState('');
  const initialTeamId = isStandalone() ? (getStandaloneTeamId() || paramTeamId) : paramTeamId;
  const teamId = resolvedTeamId || initialTeamId;

  const isPlayFromHere = !!(entryStoryflow && entryNode);

  const [ready, setReady] = useState(!isPlayFromHere);
  const [initialDecisions, setInitialDecisions] = useState({});
  const [dataLoaded, setDataLoaded] = useState(false);
  const [loadError, setLoadError] = useState('');

  // Team config
  const [gameSounds, setGameSounds] = useState(null);
  const [musicTracks, setMusicTracks] = useState(null);
  const [patreonUrl, setPatreonUrl] = useState('');
  const [itchUrl, setItchUrl] = useState('');
  const [patreonTiers, setPatreonTiers] = useState([]);
  const [authEndpoint, setAuthEndpoint] = useState('');
  const [promoEndpoint, setPromoEndpoint] = useState('');
  const [promoInfoEndpoint, setPromoInfoEndpoint] = useState('');
  const [fullGalleryTier, setFullGalleryTier] = useState('');
  const [fullStoryLogTier, setFullStoryLogTier] = useState('');
  const [instaAppName, setInstaAppName] = useState('');
  const [instaPrivateTier, setInstaPrivateTier] = useState('');
  const [instaStoriesMin, setInstaStoriesMin] = useState(2);
  const [instaStoriesMax, setInstaStoriesMax] = useState(3);
  const [instaAccounts, setInstaAccounts] = useState([]);

  // Game data
  const [allStoryflows, setAllStoryflows] = useState([]);
  const [characters, setCharacters] = useState([]);

  // Load everything at start — uses dataProvider for online/offline
  useEffect(() => {
    // In standalone mode, always load (teamId comes from the bundle)
    if (!isStandalone() && !teamId) return;
    let cancelled = false;
    (async () => {
      try {
        const bundle = await loadGameData(teamId);
        if (cancelled) return;

        // In standalone mode, resolve teamId from the bundle if needed
        if (isStandalone() && !teamId && bundle.teamId) {
          setResolvedTeamId(bundle.teamId);
        }

        // Process game config
        const gc = bundle.gameConfig || {};
        if (gc.sounds) setGameSounds(gc.sounds);
        if (gc.musicTracks) setMusicTracks(gc.musicTracks);
        setPatreonUrl(gc.patreonUrl || '');
        setItchUrl(gc.itchUrl || '');
        setPatreonTiers(gc.patreonTiers || []);
        setAuthEndpoint(gc.authEndpoint || '');
        setPromoEndpoint(gc.promoEndpoint || '');
        setPromoInfoEndpoint(gc.promoInfoEndpoint || '');
        setFullGalleryTier(gc.fullGalleryTier || '');
        setFullStoryLogTier(gc.fullStoryLogTier || '');
        setInstaAppName(gc.instaAppName || '');
        setInstaPrivateTier(gc.instaPrivateTier || '');
        setInstaStoriesMin(gc.instaStoriesMin ?? 2);
        setInstaStoriesMax(gc.instaStoriesMax ?? 3);

        // Process storyflows
        const flows = (bundle.storyflows || []).sort((a, b) => (a.number ?? 9999) - (b.number ?? 9999));
        if (!cancelled) setAllStoryflows(flows);

        // Process characters
        if (!cancelled) setCharacters(bundle.characters || []);

        // Process insta accounts
        if (!cancelled) setInstaAccounts(bundle.instaAccounts || []);

        if (!cancelled) setDataLoaded(true);
      } catch (err) {
        if (!cancelled) setLoadError(err.message || 'Failed to load game data');
      }
    })();
    return () => { cancelled = true; };
  }, [teamId]);

  // Loading screen
  if (!dataLoaded) {
    return (
      <div className="game-shell h-full w-full flex items-center justify-center bg-game-surface font-game select-none" onContextMenu={(e) => e.preventDefault()}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center gap-3"
        >
          {loadError ? (
            <>
              <div className="text-red-400 text-sm">{loadError}</div>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="px-4 py-2 rounded-xl bg-game-accent text-sm font-semibold text-game-accentText"
              >
                Retry
              </button>
            </>
          ) : (
            <>
              <div className="h-6 w-6 rounded-full border-2 border-white/20 border-t-white/70 animate-spin" />
              <div className="text-sm text-white/40">Loading game data...</div>
              <div className="text-[10px] text-white/25 max-w-[240px] text-center leading-snug mt-1">
                Tip: you can restore an older playthrough from the Saves screen.
              </div>
              <PwaDownloadOverlay />
            </>
          )}
        </motion.div>
      </div>
    );
  }

  // "Play From Here" pre-start dialog with decision configuration
  if (isPlayFromHere && !ready) {
    return (
      <PlayFromHereDialog
        teamId={teamId}
        entryStoryflow={entryStoryflow}
        entryNode={entryNode}
        allStoryflows={allStoryflows}
        onStart={(chosenDecisions) => {
          setInitialDecisions(chosenDecisions);
          setReady(true);
        }}
      />
    );
  }

  // Main game render — delegate to the appropriate template.
  // NB: h-full/w-full (not h-screen/w-screen). Standalone.js wraps us in a
  // GameFrame that clamps the content to a portrait aspect ratio box; using
  // 100vw/100vh here would break out of that box on wide desktop windows
  // (Tauri/Windows) and spill past the black pillarbox bars.
  return (
    <div
      className="game-shell h-full w-full overflow-hidden font-game select-none"
      onContextMenu={(e) => e.preventDefault()}
    >
      <ChatVisualNovel
        teamId={teamId}
        entryStoryflow={entryStoryflow}
        entryNode={entryNode}
        allStoryflows={allStoryflows}
        characters={characters}
        sounds={gameSounds}
        musicTracks={musicTracks}
        patreonUrl={patreonUrl}
        itchUrl={itchUrl}
        patreonTiers={patreonTiers}
        authEndpoint={authEndpoint}
        promoEndpoint={promoEndpoint}
        promoInfoEndpoint={promoInfoEndpoint}
        fullGalleryTier={fullGalleryTier}
        fullStoryLogTier={fullStoryLogTier}
        instaAppName={instaAppName}
        instaPrivateTier={instaPrivateTier}
        instaStoriesMin={instaStoriesMin}
        instaStoriesMax={instaStoriesMax}
        instaAccounts={instaAccounts}
        initialDecisions={initialDecisions}
      />
      <PwaInstallHint />
    </div>
  );
}
